// ProgramAnnouncementsOverlay — opens from the dashboard's announcements
// card. Reuses the HeaderOverlay primitive (blurred backdrop, centered
// linen panel, X button + ESC to close), and renders the full list of
// announcements scoped to the current program. Each row is clickable to
// expand the body inline, and clicking marks the announcement as viewed
// so the unread dot fades.

import { useEffect, useMemo, useRef, useState } from "react";
import { COLORS } from "../../../lib/brand";
import { Platform, Pressable, Text, View } from "react-native";

import { HeaderOverlay } from "../HeaderOverlay";
import {
  captainDeleteAnnouncement,
  listMyAnnouncements,
  markAnnouncementViewed,
  type MyAnnouncement,
} from "../../../lib/announcements";

const FOREST = COLORS.FOREST.hex;
const AMBER = COLORS.AMBER.hex;
const SAND_DARK = "#D9D2C5";
const TEXT = COLORS.FOREST.hex;
const TEXT_MUTED = "rgba(28,43,26,0.62)";
const TEXT_LIGHT = "rgba(28,43,26,0.45)";

export type ProgramAnnouncementsOverlayProps = {
  visible: boolean;
  onClose: () => void;
  /** Competition id used to filter announcements. */
  competitionId: string;
  /** Optional friendly competition name shown in the overlay subtitle. */
  competitionName?: string | null;
  /** Initial seed list (caller may pass the dashboard's already-fetched copy
   *  to avoid a re-fetch flicker). The overlay refreshes on open anyway. */
  seed?: MyAnnouncement[];
  /** Notifies parent so it can refresh its own copy after a viewed-toggle. */
  onChanged?: (next: MyAnnouncement[]) => void;
  /** When true, each row gets a small ✕ to delete via the captain RPC. */
  canDelete?: boolean;
};

export function ProgramAnnouncementsOverlay({
  visible,
  onClose,
  competitionId,
  competitionName,
  seed,
  onChanged,
  canDelete,
}: ProgramAnnouncementsOverlayProps) {
  const [items, setItems] = useState<MyAnnouncement[]>(seed ?? []);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Stash onChanged in a ref so we can call the latest version from inside
  // effects WITHOUT including the callback in any deps array. Otherwise
  // each parent render recreates onChanged, which retriggers the fetch
  // effect, which calls onChanged, which re-renders the parent — flicker
  // between "Loading…" and the empty state forever.
  const onChangedRef = useRef(onChanged);
  useEffect(() => {
    onChangedRef.current = onChanged;
  }, [onChanged]);

  // Re-fetch each time the overlay opens. Keeps it fresh without a
  // realtime subscription. Deps deliberately exclude onChanged (see ref).
  useEffect(() => {
    if (!visible) return;
    let alive = true;
    setLoading(true);
    void (async () => {
      try {
        const all = await listMyAnnouncements();
        if (!alive) return;
        // Program-scoped surface: only show announcements addressed to
        // THIS competition. Global ("all") announcements are admin-wide
        // and surface only under the header activity bell — they don't
        // belong inside a program's announcements view.
        const scoped = all.filter(
          (a) =>
            a.audience_kind === "competition" &&
            a.audience_competition_id === competitionId
        );
        setItems(scoped);
        onChangedRef.current?.(scoped);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [visible, competitionId]);

  const unreadCount = useMemo(() => items.filter((a) => !a.viewed).length, [items]);
  const subtitle = competitionName
    ? `${competitionName}${unreadCount > 0 ? " · " + unreadCount + " unread" : ""}`
    : unreadCount > 0
    ? unreadCount + " unread"
    : null;

  return (
    <HeaderOverlay
      visible={visible}
      onClose={onClose}
      title="Announcements"
      subtitle={subtitle}
    >
      <View style={{ paddingHorizontal: 28, paddingVertical: 20 }}>
        {loading && items.length === 0 ? (
          <Text
            style={{
              fontFamily: "DMSans_400Regular",
              fontSize: 13,
              color: TEXT_LIGHT,
              paddingVertical: 24,
            }}
          >
            Loading…
          </Text>
        ) : items.length === 0 ? (
          <View style={{ paddingVertical: 24, gap: 8 }}>
            <Text
              style={{
                fontFamily: "CormorantGaramond_500Medium",
                fontSize: 22,
                color: FOREST,
              }}
            >
              No announcements for this program yet
            </Text>
            <Text
              style={{
                fontFamily: "DMSans_400Regular",
                fontSize: 13,
                color: TEXT_MUTED,
                lineHeight: 18,
              }}
            >
              When the program host posts an announcement scoped to this competition, it will appear here. Global announcements show up under the activity bell in the header.
            </Text>
          </View>
        ) : (
          <View style={{ gap: 12 }}>
            {items.map((a) => {
              const expanded = expandedId === a.id;
              return (
                <Pressable
                  key={a.id}
                  onPress={async () => {
                    setExpandedId((prev) => (prev === a.id ? null : a.id));
                    if (!a.viewed) {
                      await markAnnouncementViewed(a.id);
                      setItems((prev) => {
                        const next = prev.map((x) =>
                          x.id === a.id ? { ...x, viewed: true } : x
                        );
                        onChangedRef.current?.(next);
                        return next;
                      });
                    }
                  }}
                  accessibilityRole="button"
                  accessibilityLabel={"Open announcement: " + a.title}
                  style={({ hovered }: { hovered?: boolean }) => [
                    {
                      backgroundColor: COLORS.WHITE.hex,
                      borderRadius: 8,
                      borderWidth: 0.5,
                      borderColor:
                        hovered && Platform.OS === "web"
                          ? "rgba(28,43,26,0.32)"
                          : SAND_DARK,
                      paddingVertical: 12,
                      paddingHorizontal: 14,
                      flexDirection: "row",
                      gap: 10,
                    },
                    Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                  ]}
                >
                  <View
                    style={{
                      width: 4,
                      borderRadius: 2,
                      backgroundColor: a.viewed ? "rgba(28,43,26,0.10)" : AMBER,
                    }}
                  />
                  <View style={{ flex: 1 }}>
                    <View
                      style={{
                        flexDirection: "row",
                        justifyContent: "space-between",
                        alignItems: "baseline",
                        gap: 8,
                      }}
                    >
                      <Text
                        style={{
                          fontFamily: a.viewed ? "DMSans_500Medium" : "DMSans_700Bold",
                          fontSize: 14,
                          color: TEXT,
                          flex: 1,
                        }}
                        numberOfLines={expanded ? 0 : 1}
                      >
                        {a.title}
                      </Text>
                      <Text
                        style={{
                          fontFamily: "DMSans_400Regular",
                          fontSize: 11,
                          color: TEXT_LIGHT,
                          letterSpacing: 0.4,
                        }}
                      >
                        {formatPostedAt(a.published_at ?? a.created_at)}
                      </Text>
                      {canDelete ? (
                        <Pressable
                          onPress={async (e) => {
                            // Don't toggle the row; just delete.
                            e.stopPropagation?.();
                            const confirmed =
                              Platform.OS === "web"
                                ? typeof window !== "undefined" &&
                                  window.confirm(
                                    "Delete this announcement? It will disappear for every team and from the activity bell."
                                  )
                                : true;
                            if (!confirmed) return;
                            try {
                              await captainDeleteAnnouncement(a.id);
                            } catch {
                              // Best-effort; row will reappear on next refetch
                              return;
                            }
                            const next = items.filter((x) => x.id !== a.id);
                            setItems(next);
                            onChangedRef.current?.(next);
                          }}
                          accessibilityRole="button"
                          accessibilityLabel="Delete announcement"
                          style={[
                            {
                              width: 22,
                              height: 22,
                              alignItems: "center",
                              justifyContent: "center",
                              borderRadius: 4,
                            },
                            Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                          ]}
                        >
                          <Text
                            style={{
                              fontFamily: "DMSans_700Bold",
                              fontSize: 14,
                              color: TEXT_LIGHT,
                              lineHeight: 14,
                            }}
                          >
                            ✕
                          </Text>
                        </Pressable>
                      ) : null}
                    </View>
                    {expanded && a.body ? (
                      <Text
                        style={{
                          marginTop: 8,
                          fontFamily: "DMSans_400Regular",
                          fontSize: 13,
                          color: "rgba(28,43,26,0.78)",
                          lineHeight: 19,
                        }}
                      >
                        {a.body}
                      </Text>
                    ) : null}
                    {expanded && !a.body ? (
                      <Text
                        style={{
                          marginTop: 8,
                          fontFamily: "DMSans_400Regular",
                          fontSize: 12.5,
                          color: TEXT_LIGHT,
                          fontStyle: "italic",
                        }}
                      >
                        No additional details.
                      </Text>
                    ) : null}
                  </View>
                </Pressable>
              );
            })}
          </View>
        )}
      </View>
    </HeaderOverlay>
  );
}

function formatPostedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay)
    return "Today \u00b7 " + d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const yest = new Date(now);
  yest.setDate(now.getDate() - 1);
  const isYest =
    d.getFullYear() === yest.getFullYear() &&
    d.getMonth() === yest.getMonth() &&
    d.getDate() === yest.getDate();
  if (isYest) return "Yesterday";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
